/**
 * SelfRegistrationModal
 *
 * A multi-step modal for patient self-registration on the program login page.
 * Reuses the EXISTING program patient registration form (EntityFormRenderer).
 *
 * Steps:
 * 1. Consent (if required by program config)
 * 2. Form filling (renders the program's published PATIENT form)
 * 3. Duplicate check (if enabled)
 * 4. Submission + success
 */
import { useState, useCallback, useMemo } from 'react';
import { UserPlus, CheckCircle, AlertTriangle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { AppModal, AppModalHeader, AppModalTitle } from '../components/ui/AppModal';
import { Button } from '../components/ui/button';
import { Input } from './ui/input';
import { AppInlineLoader } from '@/modules/shared/components/ui/AppLoading';
import { EntityFormRenderer } from '@/modules/programs/components/shared/EntityFormRenderer';
import type { EntitySelectorConfig, FormSchema } from '@/modules/programs/types/formBuilder';
import { withDefaultEntityParents, type FieldValue } from '@/modules/programs/components/workspace/dynamicFormFieldUtils';
import {
  type SelfRegistrationConfig,
  type SelfRegistrationResult,
  getSelfRegistrationHospitals,
  getSelfRegistrationHcps,
  submitSelfRegistration,
  checkDuplicatePatient,
} from '../services/selfRegistrationService';

// ─── Types ───────────────────────────────────────────────────────────

interface SelfRegistrationModalProps {
  open: boolean;
  onClose: () => void;
  programCode: string;
  config: SelfRegistrationConfig;
}

type ModalStep = 'consent' | 'form' | 'duplicate-warning' | 'submitting' | 'success';

// ─── Component ───────────────────────────────────────────────────────

export function SelfRegistrationModal({
  open,
  onClose,
  programCode,
  config,
}: SelfRegistrationModalProps) {
  const initialStep: ModalStep = config.consentRequired ? 'consent' : 'form';

  const [step, setStep] = useState<ModalStep>(initialStep);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [formData, setFormData] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<SelfRegistrationResult | null>(null);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [selectedHospitalId, setSelectedHospitalId] = useState<string>('');
  const [selectedHcpId, setSelectedHcpId] = useState<string>('');
  const [hcpSearch, setHcpSearch] = useState('');

  const { data: hospitals = [], isLoading: hospitalsLoading } = useQuery({
    queryKey: ['selfRegistrationHospitals', programCode],
    queryFn: () => getSelfRegistrationHospitals(programCode),
    enabled: open && config.enableHospitalSelection,
    staleTime: 2 * 60 * 1000,
  });

  const { data: hcps = [], isLoading: hcpsLoading } = useQuery({
    queryKey: ['selfRegistrationHcps', programCode, selectedHospitalId, hcpSearch],
    queryFn: () =>
      getSelfRegistrationHcps(programCode, {
        hospitalId:
          config.restrictHcpsBySelectedHospital && selectedHospitalId
            ? selectedHospitalId
            : undefined,
        search: hcpSearch || undefined,
      }),
    enabled:
      open
      && config.enableHcpSelection
      && (!config.restrictHcpsBySelectedHospital || !!selectedHospitalId),
    staleTime: 60 * 1000,
  });

  // Parse and normalize formSchema from config.
  // The self-registration API may return a simplified schema (sections-only, isRequired
  // instead of mandatory, missing internalName/dataType/sortOrder). Normalize it into
  // the FormSchema shape that EntityFormRenderer expects.
  const formSchema = useMemo<FormSchema | null>(() => {
    if (!config.formSchema) return null;
    let raw: Record<string, unknown>;
    if (typeof config.formSchema === 'string') {
      try { raw = JSON.parse(config.formSchema); } catch { return null; }
    } else {
      raw = config.formSchema as Record<string, unknown>;
    }

    // If the schema already has a top-level fields array, assume it's proper FormSchema
    if (Array.isArray(raw.fields) && raw.fields.length > 0) {
      const parsed = raw as unknown as FormSchema;
      return { ...parsed, fields: withDefaultEntityParents(parsed.fields) };
    }

    // Otherwise flatten sections[].fields into top-level fields array and normalize
    const sections = raw.sections as Array<{
      sectionId?: string;
      sectionTitle?: string;
      title?: string;
      fields?: Array<Record<string, unknown>>;
    }> | undefined;

    if (!Array.isArray(sections) || sections.length === 0) return null;

    let sortCounter = 0;
    const normalizedFields: FormSchema['fields'] = [];
    const normalizedSections: FormSchema['sections'] = [];

    for (const section of sections) {
      const sectionId = section.sectionId || `section_${normalizedSections.length}`;
      normalizedSections.push({
        sectionId,
        title: section.sectionTitle || section.title || sectionId,
        sortOrder: normalizedSections.length,
      });

      if (Array.isArray(section.fields)) {
        for (const f of section.fields) {
          normalizedFields.push({
            fieldId: (f.fieldId as string) || `field_${sortCounter}`,
            label: (f.label as string) || '',
            internalName: (f.internalName as string) || (f.fieldId as string) || `field_${sortCounter}`,
            fieldType: (f.fieldType as string || 'TEXT') as FormSchema['fields'][number]['fieldType'],
            dataType: (f.dataType as string || 'STRING') as FormSchema['fields'][number]['dataType'],
            mandatory: typeof f.mandatory === 'boolean' ? f.mandatory : (f.isRequired === true),
            sortOrder: typeof f.sortOrder === 'number' ? f.sortOrder : sortCounter,
            sectionId,
            helpText: f.helpText as string | undefined,
            placeholder: f.placeholder as string | undefined,
            defaultValue: f.defaultValue as string | undefined,
            options: Array.isArray(f.options)
              ? f.options.map((opt: unknown, idx: number) =>
                  typeof opt === 'string'
                    ? { label: opt, value: opt, isDefault: false, sortOrder: idx, enabled: true }
                    : { isDefault: false, sortOrder: idx, enabled: true, ...(opt as Record<string, unknown>) }
                )
              : undefined,
            entitySelectorConfig: f.entitySelectorConfig as EntitySelectorConfig | undefined,
            validationRules: Array.isArray(f.validationRules)
              ? (f.validationRules as FormSchema['fields'][number]['validationRules'])
              : undefined,
            properties: (f.properties as FormSchema['fields'][number]['properties']) || undefined,
            gridWidth: (f.gridWidth as string | undefined) as '100%' | '50%' | '33%' | '20%' | undefined,
          } as FormSchema['fields'][number]);
          sortCounter++;
        }
      }
    }

    if (normalizedFields.length === 0) return null;

    return {
      schemaVersion: (raw.schemaVersion as string) || '1.0',
      title: (raw.title as string) || config.programName || 'Patient Registration',
      description: raw.description as string | undefined,
      sections: normalizedSections,
      fields: withDefaultEntityParents(normalizedFields),
    } as FormSchema;
  }, [config.formSchema, config.programName]);

  // Detect if form schema contains ENTITY_SELECTOR fields for hospital/HCP
  // If so, skip the legacy static provider selection section
  const hasEntitySelectorFields = useMemo(() => {
    if (!formSchema?.fields) return false;
    return formSchema.fields.some(
      (f) => f.fieldType === 'ENTITY_SELECTOR' &&
        f.entitySelectorConfig &&
        (f.entitySelectorConfig.entityType === 'HOSPITAL' || f.entitySelectorConfig.entityType === 'HCP')
    );
  }, [formSchema]);

  // Helper: extract hospitalId and hcpId from formData for entity selector fields
  const resolveEntitySelectionsFromForm = useCallback((): { hospitalId?: string; hcpId?: string } => {
    if (!formSchema?.fields || !hasEntitySelectorFields) {
      return { hospitalId: selectedHospitalId || undefined, hcpId: selectedHcpId || undefined };
    }
    let hospitalId: string | undefined;
    let hcpId: string | undefined;
    for (const field of formSchema.fields) {
      if (field.fieldType !== 'ENTITY_SELECTOR' || !field.entitySelectorConfig) continue;
      const val = formData[field.internalName];
      if (!val) continue;
      if (field.entitySelectorConfig.entityType === 'HOSPITAL') {
        hospitalId = String(val);
      } else if (field.entitySelectorConfig.entityType === 'HCP') {
        hcpId = String(val);
      }
    }
    return { hospitalId, hcpId };
  }, [formSchema, formData, hasEntitySelectorFields, selectedHospitalId, selectedHcpId]);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setStep(initialStep);
    setConsentAccepted(false);
    setFormData({});
    setErrors({});
    setSubmitError('');
    setResult(null);
    setDuplicateMessage('');
    setSelectedHospitalId('');
    setSelectedHcpId('');
    setHcpSearch('');
    onClose();
  }, [initialStep, onClose]);

  // Field change handler with entity selector dependency reset
  const handleFieldChange = useCallback((fieldName: string, value: FieldValue) => {
    setFormData(prev => {
      const next = { ...prev, [fieldName]: value };
      // Reset dependent entity selector fields when a parent changes
      if (formSchema?.fields) {
        for (const f of formSchema.fields) {
          if (
            f.fieldType === 'ENTITY_SELECTOR' &&
            f.entitySelectorConfig?.parentDependencyFieldId === fieldName
          ) {
            next[f.internalName] = null;
          }
        }
      }
      return next;
    });
    // Clear field error on change
    setErrors(prev => {
      if (prev[fieldName]) {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      }
      return prev;
    });
  }, [formSchema]);

  // Validate required fields
  const validateForm = useCallback((): boolean => {
    if (!formSchema?.fields) return false;
    const newErrors: Record<string, string> = {};
    for (const field of formSchema.fields) {
      if (field.mandatory) {
        const val = formData[field.internalName];
        if (val === undefined || val === null || val === '') {
          if (field.fieldType === 'ENTITY_SELECTOR' && field.entitySelectorConfig?.entityType === 'HOSPITAL') {
            newErrors[field.internalName] = 'Please select a Hospital.';
          } else if (field.fieldType === 'ENTITY_SELECTOR' && field.entitySelectorConfig?.entityType === 'HCP') {
            newErrors[field.internalName] = 'Please select an HCP.';
          } else {
            newErrors[field.internalName] = `${field.label || field.internalName} is required`;
          }
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formSchema, formData]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    // Resolve hospital/HCP from entity selector fields or legacy static selects
    const entitySelections = resolveEntitySelectionsFromForm();
    const resolvedHospitalId = entitySelections.hospitalId || selectedHospitalId;
    const resolvedHcpId = entitySelections.hcpId || selectedHcpId;

    if (config.enableHospitalSelection && !hasEntitySelectorFields && !selectedHospitalId) {
      setSubmitError('Please select a Hospital.');
      return;
    }

    if (config.enableHcpSelection && config.requireHcpSelection && !hasEntitySelectorFields && !selectedHcpId) {
      setSubmitError('Please select an HCP.');
      return;
    }

    setSubmitError('');

    // Duplicate check if enabled
    if (config.requireDuplicateCheck && step === 'form') {
      try {
        const dupResult = await checkDuplicatePatient(programCode, formData as Record<string, unknown>);
        if (dupResult.isDuplicate) {
          setDuplicateMessage(dupResult.message || 'A patient with similar details already exists.');
          setStep('duplicate-warning');
          return;
        }
      } catch {
        // If duplicate check fails, proceed with submission
      }
    }

    // Submit registration
    setStep('submitting');
    try {
      const registrationResult = await submitSelfRegistration(programCode, {
        programCode,
        formData: formData as Record<string, unknown>,
        consentAccepted,
        hospitalId: resolvedHospitalId || undefined,
        hcpId: resolvedHcpId || undefined,
        website: honeypot,
      });
      setResult(registrationResult);
      setStep('success');
    } catch (err: unknown) {
      setStep('form');
      setSubmitError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    }
  }, [
    validateForm,
    resolveEntitySelectionsFromForm,
    hasEntitySelectorFields,
    config.enableHospitalSelection,
    config.enableHcpSelection,
    config.requireHcpSelection,
    config.requireDuplicateCheck,
    step,
    programCode,
    formData,
    consentAccepted,
    selectedHospitalId,
    selectedHcpId,
    honeypot,
  ]);

  // Force submit after duplicate warning acknowledged
  const handleForceSubmit = useCallback(async () => {
    setStep('submitting');
    setSubmitError('');
    const entitySelections = resolveEntitySelectionsFromForm();
    const resolvedHospitalId = entitySelections.hospitalId || selectedHospitalId;
    const resolvedHcpId = entitySelections.hcpId || selectedHcpId;
    try {
      const registrationResult = await submitSelfRegistration(programCode, {
        programCode,
        formData: formData as Record<string, unknown>,
        consentAccepted,
        hospitalId: resolvedHospitalId || undefined,
        hcpId: resolvedHcpId || undefined,
        website: honeypot,
      });
      setResult(registrationResult);
      setStep('success');
    } catch (err: unknown) {
      setStep('form');
      setSubmitError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    }
  }, [programCode, formData, consentAccepted, selectedHospitalId, selectedHcpId, resolveEntitySelectionsFromForm]);

  // ─── Render Steps ────────────────────────────────────────────────────

  const renderConsent = () => (
    <div className="space-y-4 p-4">
      <div className="rounded-md border border-border bg-muted/30 p-3 max-h-[300px] overflow-y-auto">
        <p className="text-xs text-foreground whitespace-pre-wrap">
          {config.consentText || 'By registering, you consent to the collection and processing of your personal health information as described in the program terms.'}
        </p>
      </div>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={consentAccepted}
          onChange={(e) => setConsentAccepted(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
        />
        <span className="text-xs text-foreground">
          I have read and agree to the terms above
        </span>
      </label>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!consentAccepted}
          onClick={() => setStep('form')}
        >
          Continue
        </Button>
      </div>
    </div>
  );

  const renderForm = () => (
    <div className="space-y-3 p-4">
      {submitError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2">
          <p className="text-xs text-destructive">{submitError}</p>
        </div>
      )}
      {/* Honeypot field — hidden from real users, traps bots */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
        <label htmlFor="sr_website">Website</label>
        <input
          id="sr_website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>
      {formSchema ? (
        <div className="max-h-[400px] overflow-y-auto pr-1">
          {(config.enableHospitalSelection || config.enableHcpSelection) && !hasEntitySelectorFields && (
            <div className="mb-3 rounded-md border border-border bg-muted/20 p-3 space-y-3">
              <p className="text-[11px] font-medium text-foreground">Provider Selection</p>

              {config.enableHospitalSelection && (
                <div className="space-y-1">
                  <label className="text-[12px] text-foreground">
                    Hospital{config.enableHospitalSelection ? ' *' : ''}
                  </label>
                  <select
                    value={selectedHospitalId}
                    onChange={(e) => {
                      const nextHospitalId = e.target.value;
                      setSelectedHospitalId(nextHospitalId);
                      if (config.restrictHcpsBySelectedHospital) {
                        setSelectedHcpId('');
                      }
                    }}
                    className="w-full h-9 rounded-md border border-border bg-background px-2 text-[12px]"
                  >
                    <option value="">Select hospital</option>
                    {hospitals.map(hospital => (
                      <option key={hospital.hospitalId} value={hospital.hospitalId}>
                        {hospital.hospitalName} {hospital.hospitalCode ? `(${hospital.hospitalCode})` : ''}
                      </option>
                    ))}
                  </select>
                  {hospitalsLoading && <p className="text-[11px] text-muted-foreground">Loading hospitals...</p>}
                </div>
              )}

              {config.enableHcpSelection && (
                <div className="space-y-1">
                  <label className="text-[12px] text-foreground">
                    HCP{config.requireHcpSelection ? ' *' : ''}
                  </label>
                  <Input
                    value={hcpSearch}
                    onChange={(e) => setHcpSearch(e.target.value)}
                    placeholder="Search HCP by name or code"
                    className="h-8 text-[12px]"
                  />
                  <select
                    value={selectedHcpId}
                    onChange={(e) => setSelectedHcpId(e.target.value)}
                    disabled={config.restrictHcpsBySelectedHospital && !selectedHospitalId}
                    className="w-full h-9 rounded-md border border-border bg-background px-2 text-[12px] disabled:opacity-60"
                  >
                    <option value="">Select HCP</option>
                    {hcps.map(hcp => (
                      <option key={hcp.hcpId} value={hcp.hcpId}>
                        {hcp.hcpName} {hcp.hcpCode ? `(${hcp.hcpCode})` : ''}{hcp.approvalEnabled ? ' - Approval Enabled' : ''}
                      </option>
                    ))}
                  </select>
                  {config.restrictHcpsBySelectedHospital && !selectedHospitalId && (
                    <p className="text-[11px] text-muted-foreground">Select a hospital first to load HCP options.</p>
                  )}
                  {hcpsLoading && <p className="text-[11px] text-muted-foreground">Loading HCPs...</p>}
                  {!hcpsLoading
                    && hcps.length > 0
                    && config.enableHcpSelection
                    && config.requireHcpSelection
                    && config.autoRouteApprovalToSelectedHcp && (
                    <p className="text-[11px] text-muted-foreground">Only approval-enabled HCPs are listed.</p>
                  )}
                </div>
              )}
            </div>
          )}

          <EntityFormRenderer
            formSchema={formSchema}
            formData={formData}
            onChange={handleFieldChange}
            errors={errors}
            readOnly={false}
            publicProgramCode={programCode}
          />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Registration form is not available. Please contact the program administrator.
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <Button variant="outline" size="sm" onClick={handleClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={!formSchema}>
          Register
        </Button>
      </div>
    </div>
  );

  const renderDuplicateWarning = () => (
    <div className="space-y-4 p-4">
      <div className="flex items-start gap-3 rounded-md border border-warning/50 bg-warning/10 p-3">
        <AlertTriangle className="w-3.5 h-3.5 text-warning mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-medium text-foreground">Possible Duplicate Found</p>
          <p className="text-xs text-muted-foreground mt-1">{duplicateMessage}</p>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setStep('form')}>
          Go Back & Edit
        </Button>
        <Button size="sm" onClick={handleForceSubmit}>
          Continue Anyway
        </Button>
      </div>
    </div>
  );

  const renderSubmitting = () => (
    <div className="flex flex-col items-center justify-center py-8 px-4 gap-3">
      <AppInlineLoader label="Loading content" />
      <p className="text-xs text-muted-foreground">Submitting your registration...</p>
    </div>
  );

  const renderSuccess = () => (
    <div className="space-y-4 p-4">
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="rounded-full bg-success/10 p-3">
          <CheckCircle className="w-3.5 h-3.5 text-success" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">Registration Submitted</p>
          <p className="text-xs text-muted-foreground mt-1">
            {result?.message || 'Your registration has been submitted for review.'}
          </p>
          {result?.patientCode && (
            <p className="text-xs text-muted-foreground mt-2">
              Reference: <span className="font-medium text-foreground">{result.patientCode}</span>
            </p>
          )}
        </div>
      </div>
      <div className="flex justify-center pt-2">
        <Button size="sm" onClick={handleClose}>
          Close
        </Button>
      </div>
    </div>
  );

  // ─── Main Render ─────────────────────────────────────────────────────

  return (
    <AppModal open={open} onClose={handleClose} size="lg" preventBackdropClose={step === 'submitting'}>
      <AppModalHeader>
        <div className="flex items-center gap-2">
          <UserPlus className="w-3.5 h-3.5 text-primary" />
          <AppModalTitle>Patient Registration — {config.programName}</AppModalTitle>
        </div>
      </AppModalHeader>

      {step === 'consent' && renderConsent()}
      {step === 'form' && renderForm()}
      {step === 'duplicate-warning' && renderDuplicateWarning()}
      {step === 'submitting' && renderSubmitting()}
      {step === 'success' && renderSuccess()}
    </AppModal>
  );
}
